import { getStandInApplicationSource } from "@/domain/resource"
import { AgentCore, AzureApps, FoundryColor, Lambda } from "@/icons"
import { APPLICATION_DRAWER_ID } from "@/organisms/Drawers/const"

import type { StandInApplicationSource } from "@/domain/application"
import type { ApplicationStandIn } from "@/domain/resource"
import type { DrawerId } from "@/hooks/useLinkDrawerTooltip"
import type { IconProps } from "@/icons"

const ICON_BY_APPLICATION_SOURCE: Record<
  StandInApplicationSource,
  (props: IconProps) => React.ReactElement
> = {
  lambda: Lambda,
  azureApp: AzureApps,
  agentcore: AgentCore,
  foundryAgent: FoundryColor
}

// How to show a resource that stands in for a Lambda function, an image-less Azure app or an
// agent deployed as code: its icon and its noun (a plural translation key: "Function" / "App" /
// "Agent"). Undefined for any other resource, including every resource from an API that sends no
// applicationSource, so callers keep their image display
export const getApplicationSourceDisplay = (
  resource?: Pick<ApplicationStandIn, "applicationSource"> | null
) => {
  const source = getStandInApplicationSource(resource)

  if (!source) return undefined

  return {
    Icon: ICON_BY_APPLICATION_SOURCE[source],
    nounKey: `applicationSource.${source}` as const
  }
}

// The noun of a list of resources: the application noun when all of them stand in for the same
// kind of application, the fallback key (e.g. images) otherwise
export const getResourcesNounKey = (
  resources: Pick<ApplicationStandIn, "applicationSource">[],
  fallbackKey: string
) => {
  const [first, ...rest] = resources.map(
    (resource) => getApplicationSourceDisplay(resource)?.nounKey
  )

  return first && rest.every((nounKey) => nounKey === first)
    ? first
    : fallbackKey
}

// The application a resource stands in for: its applicationId when it is a Lambda function, an
// image-less Azure app or an agent deployed as code. Other resources can carry an applicationId
// too (the API attributes runtime images to their applications) and keep opening their own drawer
export const getStandInApplicationId = (
  resource: Pick<ApplicationStandIn, "applicationId" | "applicationSource">
) =>
  getStandInApplicationSource(resource)
    ? resource.applicationId || undefined
    : undefined

// The drawer a resource opens: the application drawer when it stands in for an application, the
// given drawer of the resource itself otherwise
export const getResourceDrawer = (
  resource: Pick<ApplicationStandIn, "applicationId" | "applicationSource"> & {
    id: string
  },
  drawerId: DrawerId
): { drawerId: DrawerId; key: string } => {
  const applicationId = getStandInApplicationId(resource)

  return applicationId
    ? { drawerId: APPLICATION_DRAWER_ID, key: applicationId }
    : { drawerId, key: resource.id }
}
